@file:Suppress("MagicNumber")

package top.wcpe.allincore.ui.runtime.element

import top.wcpe.allincore.ui.runtime.event.ChangeEvent
import top.wcpe.allincore.ui.runtime.event.KeyNames
import top.wcpe.allincore.ui.runtime.event.TextFieldInputTarget
import top.wcpe.allincore.ui.runtime.event.UiEventHandler
import top.wcpe.allincore.ui.runtime.host.UiTask
import top.wcpe.allincore.ui.runtime.host.clipboardSink
import top.wcpe.allincore.ui.runtime.internal.text.codePointToString
import top.wcpe.allincore.ui.runtime.reactive.Effect
import top.wcpe.allincore.ui.runtime.reactive.Signal
import top.wcpe.allincore.ui.runtime.render.MultilineHitLayout
import top.wcpe.allincore.ui.runtime.render.TextLayout
import kotlin.jvm.JvmStatic

/**
 * 文本输入框（单行 / 多行）。
 * 支持基础编辑、选区、系统剪贴板、撤销/重做、多行模式。
 */
class TextField private constructor() :
    UIElement<TextField>(),
    TextFieldInputTarget {

    companion object {
        /** 修饰键位掩码（GLFW 标准位）。 */
        private const val MOD_SHIFT = 0x1
        private const val MOD_CONTROL = 0x2
        private const val MOD_ALT = 0x4
        private const val MOD_SUPER = 0x8

        @JvmStatic
        fun create(): TextField = TextField()
    }

    // ===== 委托 + 状态 =====
    private val core: TextEditCore = TextEditCore()
    private var placeholderValue: String = ""
    private var onChangeHandler: UiEventHandler<ChangeEvent<String>>? = null
    private var boundValueSignal: Signal<Any?>? = null

    // 光标样式
    private var caretWidthValue: Float = 2f
    private var caretColorValue: Int = 0
    private var caretBlinkValue: Boolean = true
    private var caretBlinkRateValue: Float = 1.2f

    // 交互状态（enabled/focused 覆写→backing 字段改名避基类方法冲突）
    private var passwordValue: Boolean = false
    private var enabledValue: Boolean = true
    private var readOnlyValue: Boolean = false
    private var maxLengthValue: Int = 0
    private var onSubmitHandler: UiTask? = null
    private var passwordCharValue: Char = '*'
    private var textAlignValue: String = "left"
    private var multilineValue: Boolean = false
    private var submitOnEnterValue: Boolean? = null

    // Task 2a 视觉覆盖
    private var textColorValue: Int? = null
    private var placeholderColorValue: Int? = null
    private var focusBorderColorValue: Int? = null
    private var textPaddingXValue: Float? = null
    private var selectionColorValue: Int? = null

    // 命中布局缓存
    private var caretXRelValue: FloatArray? = null
    private var multilineHitLayoutValue: MultilineHitLayout? = null
    private var desiredColumnXValue: Double = Double.NaN

    init {
        this.focusableField = true
        this.onCharField = UiEventHandler { ev -> defaultHandleChar(ev) }
        this.onKeyField = UiEventHandler { ev -> defaultHandleKey(ev) }
    }

    // ===== CRTP =====
    override fun self(): TextField = this

    // ===== onChar/onKey 覆写 =====
    override fun onChar(handler: UiEventHandler<top.wcpe.allincore.ui.runtime.event.CharEvent>?): TextField {
        this.onCharField = UiEventHandler { ev ->
            defaultHandleChar(ev)
            handler?.accept(ev)
        }
        return this
    }

    override fun onKey(handler: UiEventHandler<top.wcpe.allincore.ui.runtime.event.KeyEvent>?): TextField {
        this.onKeyField = UiEventHandler { ev ->
            defaultHandleKey(ev)
            handler?.accept(ev)
        }
        return this
    }

    // ===== enabled 覆写 =====
    fun enabled(v: Boolean): TextField {
        enabledValue = v
        this.focusableField = v
        return this
    }

    override fun enabled(): Boolean = enabledValue

    // ===== focused 覆写 =====
    override fun focused(v: Boolean): TextField {
        val wasFocused = super.focused()
        super.focused(v)
        if (v && !wasFocused && core.text().isNotEmpty()) {
            core.moveToEnd(false)
        }
        if (!v && wasFocused) {
            core.sealUndoUnit()
        }
        return this
    }

    // ===== value/signal/placeholder =====
    fun value(v: String?): TextField {
        val old = core.text()
        core.setText(v ?: "")
        val next = core.text()
        if (next != old) {
            boundValueSignal?.set(next)
            onChangeHandler?.accept(ChangeEvent(old, next))
        }
        return this
    }

    fun value(): String = core.text()

    fun placeholder(placeholder: String?): TextField {
        placeholderValue = placeholder ?: ""
        return this
    }

    fun placeholder(): String = placeholderValue

    fun onChange(handler: UiEventHandler<ChangeEvent<String>>): TextField {
        onChangeHandler = handler
        return this
    }

    @Suppress("UNCHECKED_CAST")
    fun bindValue(signal: Signal<*>): TextField {
        val objectSignal = signal as Signal<Any?>
        boundValueSignal = objectSignal
        val e = Effect {
            val newVal = toStringValue(objectSignal.get())
            val changed = newVal != core.text()
            if (changed) {
                core.setText(newVal)
            } else if (core.caret() > newVal.length) {
                core.setCaret(newVal.length, false)
            }
        }
        lifecycleScopeField.register(e)
        return this
    }

    // ===== 光标查询 =====
    fun cursorPos(): Int = core.caret()
    fun selectionStart(): Int = core.selectionStart()
    fun selectionEnd(): Int = core.selectionEnd()

    // ===== 命中布局 =====
    fun updateHitLayout(caretXRel: FloatArray?) {
        caretXRelValue = caretXRel
    }
    fun updateMultilineHitLayout(layout: MultilineHitLayout?) {
        multilineHitLayoutValue = layout
    }

    fun columnAt(localX: Double): Int = TextLayout.columnAtSnapshot(caretXRelValue, core.text(), localX)

    private fun offsetAtLocal(localX: Double, localY: Double): Int = if (multilineValue) {
        multilineHitLayoutValue?.offsetAt(localX, localY) ?: columnAt(localX)
    } else {
        columnAt(localX)
    }

    override fun setCaretFromLocal(localX: Double, localY: Double, extend: Boolean) {
        core.setCaret(offsetAtLocal(localX, localY), extend)
        resetDesiredColumn()
    }

    fun setCaretFromLocalX(localX: Double, extend: Boolean) {
        setCaretFromLocal(localX, 0.0, extend)
    }
    override fun selectWordAtLocal(localX: Double, localY: Double) {
        core.selectWordAt(offsetAtLocal(localX, localY))
        resetDesiredColumn()
    }
    fun selectWordAtLocalX(localX: Double) {
        selectWordAtLocal(localX, 0.0)
    }

    fun moveCaretVertical(dir: Int, extend: Boolean) {
        val layout = multilineHitLayoutValue ?: return
        if (desiredColumnXValue.isNaN()) {
            desiredColumnXValue = layout.caretXFor(core.caret())
        }
        val target = layout.verticalMove(core.caret(), dir, desiredColumnXValue)
        core.setCaret(target, extend)
    }

    private fun resetDesiredColumn() {
        desiredColumnXValue = Double.NaN
    }

    // ===== 光标样式 =====
    fun caretWidth(v: Float): TextField {
        caretWidthValue = v
        return this
    }
    fun caretWidth(): Float = caretWidthValue
    fun caretColor(v: Int): TextField {
        caretColorValue = v
        return this
    }
    fun caretColor(): Int = caretColorValue
    fun caretBlink(v: Boolean): TextField {
        caretBlinkValue = v
        return this
    }
    fun caretBlink(): Boolean = caretBlinkValue
    fun caretBlinkRate(v: Float): TextField {
        caretBlinkRateValue = v
        return this
    }
    fun caretBlinkRate(): Float = caretBlinkRateValue

    // ===== 交互状态 =====
    fun password(v: Boolean): TextField {
        passwordValue = v
        return this
    }
    fun password(): Boolean = passwordValue
    fun readOnly(v: Boolean): TextField {
        readOnlyValue = v
        return this
    }
    fun readOnly(): Boolean = readOnlyValue
    fun maxLength(v: Int): TextField {
        maxLengthValue = maxOf(0, v)
        return this
    }
    fun maxLength(): Int = maxLengthValue
    fun onSubmit(handler: UiTask?): TextField {
        onSubmitHandler = handler
        return this
    }
    fun onSubmit(): UiTask? = onSubmitHandler
    fun passwordChar(v: Char): TextField {
        passwordCharValue = v
        return this
    }
    fun passwordChar(): Char = passwordCharValue
    fun textAlign(v: String?): TextField {
        textAlignValue = v ?: "left"
        return this
    }
    fun textAlign(): String = textAlignValue
    fun multiline(v: Boolean): TextField {
        multilineValue = v
        return this
    }
    fun multiline(): Boolean = multilineValue
    fun submitOnEnter(v: Boolean?): TextField {
        submitOnEnterValue = v
        return this
    }
    fun submitOnEnter(): Boolean? = submitOnEnterValue

    // ===== Task 2a 视觉覆盖 =====
    fun textColor(v: Int?): TextField {
        textColorValue = v
        return this
    }
    fun textColor(): Int? = textColorValue
    fun placeholderColor(v: Int?): TextField {
        placeholderColorValue = v
        return this
    }
    fun placeholderColor(): Int? = placeholderColorValue
    fun focusBorderColor(v: Int?): TextField {
        focusBorderColorValue = v
        return this
    }
    fun focusBorderColor(): Int? = focusBorderColorValue
    fun textPaddingX(v: Float?): TextField {
        textPaddingXValue = v
        return this
    }
    fun textPaddingX(): Float? = textPaddingXValue
    fun selectionColor(v: Int?): TextField {
        selectionColorValue = v
        return this
    }
    fun selectionColor(): Int? = selectionColorValue

    // ===== 编辑 API =====
    fun insertText(s: String?) {
        if (s.isNullOrEmpty()) return
        val clamped = clampToMaxLength(s)
        if (clamped.isEmpty()) return
        val old = core.text()
        core.typeText(clamped)
        syncReactive(old)
    }

    private fun pasteText(s: String) {
        if (s.isEmpty()) return
        val clamped = clampToMaxLength(s)
        if (clamped.isEmpty()) return
        val old = core.text()
        core.pasteText(clamped)
        syncReactive(old)
    }

    fun deleteBackward() {
        val old = core.text()
        core.deleteBackward()
        syncReactive(old)
    }
    fun deleteForward() {
        val old = core.text()
        core.deleteForward()
        syncReactive(old)
    }
    fun moveCursor(delta: Int) {
        core.moveCaret(delta, false)
    }
    fun moveCursorToStart() {
        core.moveToStart(false)
    }
    fun moveCursorToEnd() {
        core.moveToEnd(false)
    }

    // ===== 私有 =====
    private fun defaultHandleChar(ev: top.wcpe.allincore.ui.runtime.event.CharEvent) {
        if (!enabledValue || readOnlyValue) return
        val cp = ev.codepoint
        if (ev.codepoint < 0x20 && !(multilineValue && ev.codepoint == 0x0A)) return
        insertText(codePointToString(ev.codepoint))
    }

    @Suppress("CyclomaticComplexMethod")
    private fun defaultHandleKey(ev: top.wcpe.allincore.ui.runtime.event.KeyEvent) {
        if (ev.action() == 0) return
        if (!enabledValue) return
        val mods = ev.modifiers()
        val ctrl = (mods and (MOD_CONTROL or MOD_SUPER)) != 0
        val shift = (mods and MOD_SHIFT) != 0
        val handled = when {
            ev.key() == KeyNames.ENTER || ev.key() == KeyNames.NUMPAD_ENTER -> handleEnter(ctrl)
            ctrl -> handleCtrlKey(ev.key(), shift)
            else -> handleEditingKey(ev.key(), shift)
        }
        if (handled) ev.consume()
    }

    private fun handleEditingKey(key: String, shift: Boolean): Boolean = when (key) {
        KeyNames.BACKSPACE -> true.also { if (!readOnlyValue) deleteBackward() }
        KeyNames.DELETE -> true.also { if (!readOnlyValue) deleteForward() }
        KeyNames.LEFT -> true.also {
            core.moveCaret(-1, shift)
            resetDesiredColumn()
        }
        KeyNames.RIGHT -> true.also {
            core.moveCaret(1, shift)
            resetDesiredColumn()
        }
        KeyNames.HOME -> true.also {
            core.moveToStart(shift)
            resetDesiredColumn()
        }
        KeyNames.END -> true.also {
            core.moveToEnd(shift)
            resetDesiredColumn()
        }
        KeyNames.UP -> multilineValue.also { if (it) moveCaretVertical(-1, shift) }
        KeyNames.DOWN -> multilineValue.also { if (it) moveCaretVertical(1, shift) }
        else -> false
    }

    private fun handleEnter(ctrl: Boolean): Boolean {
        if (!multilineValue) {
            if (ctrl || submitOnEnterValue == false) return false
            onSubmitHandler?.run()
            return true
        }
        val submit = submitOnEnterValue == true || (submitOnEnterValue == null && ctrl)
        if (submit) {
            onSubmitHandler?.run()
            return true
        }
        if (readOnlyValue) return false
        insertText("\n")
        return true
    }

    private fun handleCtrlKey(key: String, shift: Boolean): Boolean {
        when (key) {
            KeyNames.A -> core.selectAll()
            KeyNames.C -> copySelectionToClipboard()
            KeyNames.X -> cutSelectionToClipboard()
            KeyNames.V -> return pasteFromClipboard()
            KeyNames.Z -> if (shift) redo() else undo()
            KeyNames.Y -> redo()
            else -> return false
        }
        return true
    }

    private fun undo() {
        if (!readOnlyValue) {
            val old = core.text()
            core.undo()
            syncReactive(old)
        }
    }
    private fun redo() {
        if (!readOnlyValue) {
            val old = core.text()
            core.redo()
            syncReactive(old)
        }
    }

    private fun copySelectionToClipboard() {
        if (!core.hasSelection()) return
        clipboardSink().setText(core.selectedText())
    }

    private fun cutSelectionToClipboard() {
        if (readOnlyValue) return
        if (!core.hasSelection()) return
        clipboardSink().setText(core.selectedText())
        deleteBackward()
    }

    private fun pasteFromClipboard(): Boolean {
        val clipboard = clipboardSink()
        if (!clipboard.supportsSynchronousPaste()) return false
        if (readOnlyValue) return true
        val pasted = clipboard.text() ?: return true
        if (pasted.isEmpty()) return true
        val normalized = if (multilineValue) {
            pasted.replace(Regex("\\r\\n|\\r"), "\n")
        } else {
            pasted.replace(Regex("\\r\\n|[\\r\\n]"), " ")
        }
        pasteText(normalized)
        return true
    }

    private fun clampToMaxLength(s: String): String {
        if (maxLengthValue <= 0) return s
        val len = core.text().length
        val selLen = core.selectionEnd() - core.selectionStart()
        var room = maxLengthValue - (len - selLen)
        if (room <= 0) return ""
        if (s.length <= room) return s
        if (s[room].isLowSurrogate() && s[room - 1].isHighSurrogate()) room--
        return s.substring(0, room)
    }

    private fun syncReactive(old: String) {
        val next = core.text()
        if (next == old) return
        resetDesiredColumn()
        boundValueSignal?.set(next)
        onChangeHandler?.accept(ChangeEvent(old, next))
    }

    private fun toStringValue(v: Any?): String = v?.toString() ?: ""
}
